import { api, descargarArchivo, getActiveTenantHeader, subirArchivo } from "@/lib/api"
import type { Plantilla, Previsualizacion, ResultadoCarga } from "@/routes/carga/mapping"

/**
 * La carga de maestros y datos por archivo.
 *
 *   GET  /carga/plantillas                  que se puede cargar y con que columnas
 *   GET  /carga/{plantilla}/ejemplo.csv     el archivo para rellenar
 *   POST /carga/{plantilla}/previsualizar   que pasaria. NO escribe.
 *   POST /carga/{plantilla}                 escribe (exige motivo)
 *
 * NO hay modo mock. Las demas pantallas lo tienen porque sirven para mirar
 * datos, y un mock deja ver la forma; esta pantalla ESCRIBE maestros, y un
 * mock que responde «12 creadas» sin haber creado nada es exactamente el
 * defecto que este producto no puede permitirse. Sin backend, falla y se ve.
 */

/**
 * El tenant viaja en `X-Tenant-Id`, como en toda la SPA.
 *
 * Estos endpoints lo declaran como `tenant_id: UUID = Query(...)`, igual que
 * los otros cuarenta y siete, y un middleware del backend copia el header al
 * query string antes de enrutar (ver `tenant_del_header` en `main.py`). No
 * hace falta pasarlo a mano.
 *
 * Lo que si hace falta es NO llegar hasta el servidor sin tenant: el 422 que
 * responde entonces nombra un campo que no esta en ningun formulario, y manda
 * a buscar el problema al sitio equivocado. La subida de un archivo grande
 * ademas tarda en fallar.
 */
function exigirTenantActivo(): void {
  if (!getActiveTenantHeader()) {
    throw new Error(
      "No hay cliente activo. Elige uno en la barra superior antes de cargar un archivo.",
    )
  }
}

export function fetchPlantillas(): Promise<Plantilla[]> {
  return api.get<Plantilla[]>("/carga/plantillas")
}

/**
 * La plantilla para rellenar, EN EXCEL.
 *
 * Cesar: «que las plantillas de ejemplo se descarguen en excel en vez de csv».
 * Nadie rellena un CSV a mano, y el xlsx que se descarga ya trae los anchos
 * puestos, la cabecera fija y una segunda hoja con qué es obligatorio y qué
 * valores admite cada columna.
 *
 * Y lo que no se ve: **las columnas de texto llevan formato `@`**, que es lo
 * único que impide que Excel convierta un RUC `00123456789` en 123456789. La
 * carga acepta el xlsx de vuelta, así que el ciclo cierra — dar un formato para
 * rellenar que después se rechaza sería peor que no darlo.
 *
 * El CSV sigue existiendo en `/carga/{id}/ejemplo.csv` para quien exporta desde
 * otro sistema; lo que cambia es cuál se ofrece.
 */
export function descargarEjemplo(plantillaId: string): Promise<void> {
  return descargarArchivo(`/carga/${plantillaId}/ejemplo.xlsx`, `${plantillaId}-ejemplo.xlsx`)
}

export function previsualizar(plantillaId: string, archivo: File): Promise<Previsualizacion> {
  const form = new FormData()
  form.append("archivo", archivo)
  exigirTenantActivo()
  return subirArchivo<Previsualizacion>(`/carga/${plantillaId}/previsualizar`, form)
}

export function cargar(
  plantillaId: string,
  archivo: File,
  motivo: string,
  continuarConErrores: boolean,
): Promise<ResultadoCarga> {
  const form = new FormData()
  form.append("archivo", archivo)
  form.append("motivo", motivo)
  // El backend lo lee con `bool` de FastAPI: "true"/"false" son los literales
  // que acepta. Mandar el booleano de JS lo convertiria a "true"/"false" igual,
  // pero dejarlo explicito evita que un cambio de tipado lo rompa en silencio.
  form.append("continuar_con_errores", continuarConErrores ? "true" : "false")
  exigirTenantActivo()
  return subirArchivo<ResultadoCarga>(`/carga/${plantillaId}`, form)
}
